import api, { TOKEN_STORAGE_KEY } from '../api/client';

// Incident capture is separate from geocoding. Never replace GPS with an address,
// a previous incident's location, or an old/unbounded browser position.
export function usableIncidentFix(value, now = Date.now()) {
  if (!value) return null;
  const latitude = value.latitude ?? value.lat;
  const longitude = value.longitude ?? value.lng;
  const observedAt = typeof value.observedAt === 'number' ? value.observedAt : Date.parse(value.locationObservedAtUtc);
  const accuracy = value.accuracy ?? value.accuracyMeters;
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude) || Math.abs(latitude) > 90 || Math.abs(longitude) > 180 ||
      !Number.isFinite(observedAt) || observedAt > now + 30000 || now - observedAt > 15000 ||
      !Number.isFinite(accuracy) || accuracy < 0 || accuracy > 250) return null;
  return { latitude, longitude, heading: value.heading, observedAt, accuracy };
}

export async function captureIncidentCoordinates({ userId, getCurrentFix = () => null }) {
  const token = localStorage.getItem(TOKEN_STORAGE_KEY);
  const assertOwner = () => {
    if (!token || localStorage.getItem(TOKEN_STORAGE_KEY) !== token) throw new Error('Account changed while locating the call. Retry after signing in.');
  };
  const canonical = async () => {
    try {
      const response = await api.get('/status/active-units', { timeout: 3000, expectedSessionToken: token });
      return Array.isArray(response.data) ? response.data.find(unit => unit.userId === userId) : null;
    } catch { return null; }
  };
  const browser = new Promise(resolve => {
    if (!navigator.geolocation) return resolve(null);
    let settled = false;
    const finish = value => { if (!settled) { settled = true; clearTimeout(timer); resolve(value); } };
    const timer = setTimeout(() => finish(null), 10500);
    try {
      navigator.geolocation.getCurrentPosition(position => finish({ ...position.coords,
        latitude: position.coords.latitude, longitude: position.coords.longitude,
        accuracy: position.coords.accuracy, heading: position.coords.heading, observedAt: position.timestamp }),
      () => finish(null), { enableHighAccuracy: true, maximumAge: 0, timeout: 10000 });
    } catch { finish(null); }
  });
  assertOwner();
  const [position, firstUnit] = await Promise.all([browser, canonical()]);
  assertOwner();
  // Re-read after a failed browser request; a watch/other device may have supplied
  // a fresh fix during the wait. Never use an expired React closure as fallback.
  const unit = usableIncidentFix(position) ? firstUnit : await canonical();
  assertOwner();
  const phone = unit?.locationFromMobile === true ? usableIncidentFix(unit) : null;
  if (phone) return phone;
  const fixes = [position, getCurrentFix(), unit].map(value => usableIncidentFix(value)).filter(Boolean);
  return fixes.sort((a, b) => a.accuracy - b.accuracy || b.observedAt - a.observedAt)[0] || null;
}
